import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Group, Progress, Stack, Text, Tooltip } from '@mantine/core';
import { FormattedMessage, useIntl } from 'react-intl';

import { messages } from './messages';

type ShipmentLimitProgressProps = {
  shipmentLimit: number;
  totalUsage: number;
  isAtLimit: boolean;
  isHighLimitWarning: boolean;
  isMediumLimitWarning: boolean;
};

const getProgressColor = (isAtLimit: boolean, isHighLimitWarning: boolean, isMediumLimitWarning: boolean) => {
  if (isAtLimit || isHighLimitWarning) return 'var(--adl-status-alert-medium)';
  if (isMediumLimitWarning) return 'var(--adl-status-warning-medium)';
  return undefined;
};

export const ShipmentLimitProgress = ({
  shipmentLimit,
  totalUsage,
  isAtLimit,
  isHighLimitWarning,
  isMediumLimitWarning,
}: ShipmentLimitProgressProps) => {
  const intl = useIntl();
  const roundedUsagePercentage = Math.round((totalUsage / shipmentLimit) * 100);
  const barPercentageToUse = shipmentLimit === 0 ? 0 : Math.max(roundedUsagePercentage, 5); // show at least some bar if they have any usage
  const usagePercentage = shipmentLimit > 0 ? Math.min(barPercentageToUse, 100) : 100;
  const progressColor = getProgressColor(isAtLimit, isHighLimitWarning, isMediumLimitWarning);

  return (
    <Stack gap='xs'>
      <Group gap='xs' justify='space-between'>
        <Group gap={4}>
          <Text fw={600} size='sm'>
            <FormattedMessage {...messages.monthlyShipmentLimit} />
          </Text>
          <Tooltip label={intl.formatMessage(messages.monthlyShipmentLimitTooltip)}>
            <FontAwesomeIcon icon={faInfoCircle} />
          </Tooltip>
        </Group>
        <Text c='dimmed' size='xs'>
          <FormattedMessage
            {...messages.usageProgress}
            values={{ shipmentLimit: intl.formatNumber(shipmentLimit), totalUsage: intl.formatNumber(totalUsage) }}
          />
        </Text>
      </Group>
      <Progress color={progressColor} value={usagePercentage} />
    </Stack>
  );
};
